/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {ColDef} from 'ag-grid-community';
import {CommonHelperService} from '../service/common-helper.service';
import {escapeHtml} from './html.util';

export interface CadenceMergeMatchField {
  path: string;
  candidateMatch: boolean;
}

export type CadenceMergeRowMode = 'matched' | 'changed' | 'new' | 'deleted';

export interface DeviceIoDiffFieldConfig {
  snapshotField: string;
  /** Aggregator flat live* field on the saved row (for example liveDeviceIoName). */
  liveField?: string;
  /** Nested path on the saved row for live values (for example liveDeviceIo.devicePinName). */
  livePath?: string;
}

/** Maps saved-row live* diff metadata to snapshot columns for Aggregator IO Matrix sync preview. */
export const AGGREGATOR_DEVICE_IO_DIFF_FIELDS: DeviceIoDiffFieldConfig[] = [
  {snapshotField: 'deviceName', liveField: 'liveDeviceName'},
  {snapshotField: 'deviceIoName', liveField: 'liveDeviceIoName'},
  {snapshotField: 'deviceIoType', liveField: 'liveDeviceIoType'},
  {snapshotField: 'deviceIoValue', liveField: 'liveDeviceIoValue'},
  {snapshotField: 'circuitNumber', liveField: 'liveCircuitNumber'},
  {snapshotField: 'pinType', liveField: 'livePinType'}
];

/** Powertrain IO Matrix nested deviceIo snapshot vs liveDeviceIo diff fields. */
export const POWERTRAIN_DEVICE_IO_DIFF_FIELDS: DeviceIoDiffFieldConfig[] = [
  {snapshotField: 'deviceIo.ioDeviceName', livePath: 'liveDeviceIo.ioDeviceName'},
  {snapshotField: 'deviceIo.devicePinName', livePath: 'liveDeviceIo.devicePinName'},
  {snapshotField: 'deviceIo.deviceIOType', livePath: 'liveDeviceIo.deviceIOType'},
  {snapshotField: 'deviceIo.deviceIOValue', livePath: 'liveDeviceIo.deviceIOValue'},
  {snapshotField: 'deviceIo.circuit.circNO', livePath: 'liveDeviceIo.circuit.circNO'},
  {snapshotField: 'deviceIo.pinType', livePath: 'liveDeviceIo.pinType'}
];

function asRowRecord(row: object): Record<string, unknown> {
  return row as Record<string, unknown>;
}

function readRowFieldRaw(row: object, path: string): string {
  return String(CommonHelperService.getDeepValue(row, path) ?? '').trim();
}

function readRowFieldNormalized(row: object, path: string): string {
  return normalizeCadenceMergeValue(CommonHelperService.getDeepValue(row, path));
}

function writeRowField(row: object, path: string, value: string): void {
  CommonHelperService.setDeepValue(row, path, value);
}

/** Live Device IO parent id, or undefined when the row carries no usable link. */
function readLiveDeviceIoFk(row: object): number | undefined {
  const fk = asRowRecord(row)['fkNetRptIoMapId'] as number | null | undefined;
  return fk != null && fk > 0 ? fk : undefined;
}

function indexLiveRowsByFk<T extends object>(live: T[]): Map<number, T> {
  const liveByFk = new Map<number, T>();
  for (const liveRow of live) {
    const fk = readLiveDeviceIoFk(liveRow);
    if (fk !== undefined) {
      liveByFk.set(fk, liveRow);
    }
  }
  return liveByFk;
}

/** The backend's live value for a diff field: flat live* field, nested live path, or the live row. */
function readLiveValueRaw<T extends object>(
  savedRow: T,
  liveMatch: T | undefined,
  field: DeviceIoDiffFieldConfig
): string {
  if (field.livePath) {
    return readRowFieldRaw(savedRow, field.livePath);
  }
  if (field.liveField) {
    return readRowFieldRaw(savedRow, field.liveField);
  }
  if (liveMatch) {
    return readRowFieldRaw(liveMatch, field.snapshotField);
  }
  return '';
}

/**
 * Raw live value that should replace the snapshot value, or undefined when the field has not drifted.
 * The backend's live* metadata wins; the matched live row is only consulted when that metadata agrees
 * with the snapshot, so a stale annotation cannot hide a real difference.
 */
function resolveDriftedRawValue<T extends object>(
  savedRow: T,
  liveMatch: T | undefined,
  field: DeviceIoDiffFieldConfig
): string | undefined {
  const snapshotValue = readRowFieldNormalized(savedRow, field.snapshotField);
  const liveValueRaw = readLiveValueRaw(savedRow, liveMatch, field);
  if (normalizeCadenceMergeValue(liveValueRaw) !== snapshotValue) {
    return liveValueRaw;
  }
  if (!liveMatch) {
    return undefined;
  }
  const fromLiveRaw = readRowFieldRaw(liveMatch, field.snapshotField);
  return normalizeCadenceMergeValue(fromLiveRaw) !== snapshotValue ? fromLiveRaw : undefined;
}

/** Snapshot clone with drifted fields overwritten by live values and a change map for the grid. */
function buildChangedDiffRow<T extends object>(
  savedRow: T,
  liveMatch: T | undefined,
  diffFields: DeviceIoDiffFieldConfig[]
): T {
  const updated = structuredClone(savedRow) as T;
  const changes = new Map<string, string>();
  for (const field of diffFields) {
    const driftedRaw = resolveDriftedRawValue(savedRow, liveMatch, field);
    if (driftedRaw === undefined) {
      continue;
    }
    const snapshotDisplay = readRowFieldRaw(savedRow, field.snapshotField);
    writeRowField(updated, field.snapshotField, driftedRaw);
    changes.set(field.snapshotField, `Original Value: ${snapshotDisplay}`);
  }
  asRowRecord(updated)['changes'] = changes;
  asRowRecord(updated)['originalRowData'] = structuredClone(savedRow);
  return updated;
}

/**
 * Preview row for one saved snapshot row. Records the live rows it consumed in {@code matchedLiveFks}
 * so the caller can offer the leftovers as new; a deleted row deliberately releases its live match.
 */
function buildDiffRowForSavedRow<T extends object>(
  savedRow: T,
  liveByFk: Map<number, T>,
  matchedLiveFks: Set<number>,
  diffFields: DeviceIoDiffFieldConfig[],
  decorate: (row: T, mode: CadenceMergeRowMode, original?: T) => T
): T {
  const mode = String(asRowRecord(savedRow)['mergeMode'] ?? 'matched').toLowerCase();
  if (mode === 'deleted') {
    return decorate(structuredClone(savedRow), 'deleted', savedRow);
  }

  const fk = readLiveDeviceIoFk(savedRow);
  const liveMatch = fk !== undefined ? liveByFk.get(fk) : undefined;
  if (mode === 'changed') {
    if (fk !== undefined && liveMatch) {
      matchedLiveFks.add(fk);
    }
    return decorate(buildChangedDiffRow(savedRow, liveMatch, diffFields), 'changed', savedRow);
  }

  if (fk !== undefined) {
    matchedLiveFks.add(fk);
  }
  return decorate(structuredClone(savedRow), 'matched', savedRow);
}

/**
 * Builds a same-cadence Device IO sync preview from saved snapshot rows annotated by the backend
 * ({@code mergeMode}, live* diff fields) plus the live Device IO rows for the cadence.
 */
export function buildDeviceIoDiffRows<T extends object>(
  savedRows: T[],
  liveRows: T[],
  diffFields: DeviceIoDiffFieldConfig[],
  decorate: (row: T, mode: CadenceMergeRowMode, original?: T) => T
): T[] {
  const saved = savedRows || [];
  const live = liveRows || [];
  const liveByFk = indexLiveRowsByFk(live);
  const matchedLiveFks = new Set<number>();
  const result: T[] = [];

  for (const savedRow of saved) {
    result.push(buildDiffRowForSavedRow(savedRow, liveByFk, matchedLiveFks, diffFields, decorate));
  }

  for (const liveRow of live) {
    const fk = readLiveDeviceIoFk(liveRow);
    if (fk !== undefined && matchedLiveFks.has(fk)) {
      continue;
    }
    result.push(decorate(structuredClone(liveRow), 'new', undefined));
  }

  return result;
}

export function extractMergeOriginalValue(changeEntry: string): string {
  const prefix = 'Original Value: ';
  if (!changeEntry || !changeEntry.startsWith(prefix)) {
    return '';
  }
  return changeEntry.substring(prefix.length);
}

/** Inline merge preview HTML shared by powertrain and aggregator IO matrix grids. */
export function renderCadenceMergeCellHtml(displayValue: string, changeEntry: string | undefined): string {
  const value = escapeHtml(String(displayValue ?? ''));
  const oldValue = escapeHtml(extractMergeOriginalValue(String(changeEntry || '')));
  if (!oldValue) {
    return value;
  }
  return `<span class="merge-cell-inline">`
    + `<span class="merge-current-value">${value}</span>`
    + `<span class="merge-original-value"> (was ${oldValue})</span>`
    + `</span>`;
}

/**
 * Preferred merge cell renderer output for ag-grid: a DOM node so theme CSS applies reliably
 * (HTML strings are not styled consistently across all grid hosts).
 */
export function renderCadenceMergeCellContent(
  displayValue: string,
  changeEntry: string | undefined
): string | HTMLElement {
  const value = String(displayValue ?? '');
  const oldValue = extractMergeOriginalValue(String(changeEntry || ''));
  if (!oldValue) {
    return value;
  }
  if (typeof document === 'undefined') {
    return renderCadenceMergeCellHtml(value, changeEntry);
  }
  const wrapper = document.createElement('span');
  wrapper.className = 'merge-cell-inline';

  const current = document.createElement('span');
  current.className = 'merge-current-value';
  current.textContent = value;

  const original = document.createElement('span');
  original.className = 'merge-original-value';
  original.textContent = ` (was ${oldValue})`;

  wrapper.appendChild(current);
  wrapper.appendChild(original);
  return wrapper;
}

/** ag-grid tooltip for IO matrix merge/sync preview rows and changed cells. */
export function getIoMatrixMergeCellTooltip(
  params: { data?: { state?: string; changes?: Map<string, string> } },
  field?: string
): string | undefined {
  const row = params?.data;
  if (!row) {
    return undefined;
  }
  if (row.state === 'deleted') {
    return 'Deleted from live Device IO.';
  }
  const fieldKey = field || '';
  if (!fieldKey) {
    return undefined;
  }
  const changeEntry = row.changes?.get(fieldKey);
  if (!changeEntry) {
    return undefined;
  }
  const oldValue = extractMergeOriginalValue(String(changeEntry));
  if (oldValue) {
    return `Modified — was: ${oldValue}`;
  }
  return 'Modified';
}

/** Ensures deleted-row and changed-cell merge tooltips apply on every column. */
export function wireDefaultIoMatrixMergeTooltips(defaultColDef: ColDef): ColDef {
  const existing = defaultColDef.tooltipValueGetter;
  return {
    ...defaultColDef,
    tooltipValueGetter: (params) => {
      const colDef = params.colDef;
      const field = colDef && 'field' in colDef ? String(colDef.field || '') : '';
      const mergeTooltip = getIoMatrixMergeCellTooltip(params, field);
      if (mergeTooltip) {
        return mergeTooltip;
      }
      if (typeof existing === 'function') {
        return existing(params);
      }
      return undefined;
    }
  };
}

export function normalizeCadenceMergeValue(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

export function getCadenceMergeFieldValue(row: unknown, fieldPath: string): string {
  return normalizeCadenceMergeValue(CommonHelperService.getDeepValue(row as any, fieldPath));
}

export function rowsMatchByCandidateFields<T>(
  left: T,
  right: T,
  matchFields: CadenceMergeMatchField[]
): boolean {
  return matchFields
    .filter(field => field.candidateMatch)
    .some(field => {
      const leftValue = getCadenceMergeFieldValue(left, field.path);
      const rightValue = getCadenceMergeFieldValue(right, field.path);
      return !!leftValue && !!rightValue && leftValue === rightValue;
    });
}

export function calculateCadenceMergeClosenessScore<T>(
  left: T,
  right: T | undefined,
  matchFields: CadenceMergeMatchField[]
): number {
  if (!right) {
    return -1;
  }
  let score = 0;
  const totalFields = matchFields.length;
  matchFields.forEach((field, index) => {
    const leftValue = getCadenceMergeFieldValue(left, field.path);
    const rightValue = getCadenceMergeFieldValue(right, field.path);
    if (!leftValue || !rightValue || leftValue !== rightValue) {
      return;
    }
    score += totalFields - index;
  });
  return score;
}

/**
 * Merges saved matrix rows onto the latest Device IO cadence rows using candidate-field matching
 * (pin name and/or circuit number) with closeness tie-breaks — the same algorithm as powertrain IO Matrix.
 */
export function buildCadenceMergeRows<T>(
  latestRows: T[],
  savedRows: T[],
  matchFields: CadenceMergeMatchField[],
  decorate: (latestRow: T, matchedSavedRow: T | undefined, mode: CadenceMergeRowMode) => T
): T[] {
  const latest = latestRows || [];
  const saved = savedRows || [];
  if (!saved.length) {
    return latest.map(row => decorate(row, undefined, 'new'));
  }

  const savedByIndex = new Map<number, T>();
  saved.forEach((row, index) => savedByIndex.set(index, row));
  const usedSavedIndexes = new Set<number>();
  const resultRows: T[] = [];

  latest.forEach(latestRow => {
    const candidateIndexes: number[] = [];
    saved.forEach((savedRow, savedIndex) => {
      if (usedSavedIndexes.has(savedIndex)) {
        return;
      }
      if (rowsMatchByCandidateFields(latestRow, savedRow, matchFields)) {
        candidateIndexes.push(savedIndex);
      }
    });

    if (!candidateIndexes.length) {
      resultRows.push(decorate(latestRow, undefined, 'new'));
      return;
    }

    const sortedByCloseness = candidateIndexes
      .map(savedIndex => ({
        savedIndex,
        score: calculateCadenceMergeClosenessScore(latestRow, savedByIndex.get(savedIndex), matchFields)
      }))
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        return a.savedIndex - b.savedIndex;
      });
    const winner = sortedByCloseness[0];
    const matchedSavedRow = winner ? savedByIndex.get(winner.savedIndex) : undefined;
    if (!matchedSavedRow) {
      resultRows.push(decorate(latestRow, undefined, 'new'));
      return;
    }

    usedSavedIndexes.add(winner.savedIndex);
    resultRows.push(decorate(latestRow, matchedSavedRow, 'matched'));
  });

  saved.forEach((savedRow, savedIndex) => {
    if (usedSavedIndexes.has(savedIndex)) {
      return;
    }
    resultRows.push(decorate(savedRow, savedRow, 'deleted'));
  });

  return resultRows;
}
